import React, { useState } from 'react';
import { toast } from 'sonner';
import { KeyRound, RefreshCw, Trash2, Copy, Plus, ShieldCheck, CheckCheck, Eraser } from 'lucide-react';
import { API, Card, DataTable, Empty, Spin, useApi, authHeaders, fmtWhen } from './SecurityShared';

const BASE = '/api/admin/security/service-accounts';

/**
 * Service accounts — API keys for machine clients.
 * The key is shown exactly once, right after creation.
 */
export function ServiceAccountsTab({ token }) {
  const { data, loading, reload } = useApi(token, BASE);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [fresh, setFresh] = useState(null);     // { api_key, name }
  const [openCalls, setOpenCalls] = useState(null);

  const create = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) { toast.error('Give the key a name'); return; }
    setCreating(true);
    try {
      const r = await fetch(`${API}${BASE}`, {
        method: 'POST',
        headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), preset: 'original_files_bot' }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(j.detail || 'Could not create the key'); return; }
      setFresh(j);
      setName('');
      reload();
    } finally { setCreating(false); }
  };

  const revoke = async (row) => {
    if (!window.confirm(`Revoke "${row.name}"? The bot using it stops working immediately.`)) return;
    const r = await fetch(`${API}${BASE}/${row.id}/revoke`, { method: 'POST', headers: authHeaders(token) });
    if (!r.ok) { toast.error('Failed'); return; }
    toast.success('Key revoked');
    reload();
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success('Copied');
  };

  return (
    <>
      <Card title="Create an API key" testid="sec-svc-create-card"
        hint="Scope: list orders, read one order, attach a modified file, change status — and only orders whose service is “Original file purchase”. Nothing else is reachable with this key.">
        <form onSubmit={create} className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="original-file bot" data-testid="sec-svc-name"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
          </div>
          <button type="submit" disabled={creating} data-testid="sec-svc-create"
            className="px-3 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 disabled:opacity-50">
            <Plus className="w-3.5 h-3.5" /> {creating ? 'Creating…' : 'Create key'}
          </button>
        </form>

        {fresh && (
          <div className="mt-3 rounded-xl border border-emerald-300 bg-emerald-50 p-3" data-testid="sec-svc-fresh-key">
            <p className="text-xs font-bold text-emerald-800 flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4" /> Copy the key now — it is not shown again
            </p>
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 font-mono text-[11px] bg-white border border-emerald-200 rounded px-2 py-1.5 break-all">
                {fresh.api_key}
              </code>
              <button onClick={() => copy(fresh.api_key)} data-testid="sec-svc-copy"
                className="p-1.5 rounded border border-emerald-300 bg-white"><Copy className="w-3.5 h-3.5" /></button>
            </div>
            <p className="mt-2 text-[11px] text-emerald-800">
              In the bot: drop the login request and send the header{' '}
              <code className="font-mono">X-API-Key: {fresh.key_prefix}…</code> with every call.
            </p>
            <button onClick={() => setFresh(null)} className="mt-2 text-[11px] font-bold text-emerald-700 underline">
              I saved it
            </button>
          </div>
        )}
      </Card>

      <Card title="Keys" testid="sec-svc-list-card"
        right={<button onClick={reload} className="p-1.5 rounded-lg border border-slate-300" data-testid="sec-svc-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /></button>}>
        {loading ? <Spin /> : !data?.rows?.length ? <Empty testid="sec-svc-empty">No API keys yet</Empty> : (
          <DataTable testid="sec-svc-table" rows={data.rows}
            rowKey={(r) => r.id} rowTestid={(r, i) => `sec-svc-row-${i}`}
            cols={[
              { key: 'name', label: 'Name', primary: true,
                render: (r) => (
                  <span className="font-semibold text-slate-800 flex items-center gap-1.5">
                    <KeyRound className="w-3.5 h-3.5 text-slate-400" /> {r.name}
                  </span>) },
              { key: 'state', label: 'State', primary: true,
                render: (r) => (
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                    r.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'}`}>
                    {r.active ? 'active' : 'revoked'}
                  </span>) },
              { key: 'prefix', label: 'Key', tdClass: 'font-mono text-slate-500',
                render: (r) => `${r.key_prefix}…` },
              { key: 'scope', label: 'Scope', tdClass: 'text-slate-600',
                render: (r) => r.service_filter || 'all' },
              { key: 'calls', label: 'Calls 24 h',
                render: (r) => (
                  <span className="tabular-nums font-bold text-slate-700">
                    {r.calls_24h}
                    {!!r.denied_24h && <span className="ml-1 text-rose-600 font-bold">({r.denied_24h} denied)</span>}
                  </span>) },
              { key: 'last', label: 'Last used', tdClass: 'text-slate-500 whitespace-nowrap',
                render: (r) => (r.last_used_at ? fmtWhen(r.last_used_at) : 'never') },
              { key: 'actions', label: 'Actions', right: true, full: true,
                render: (r, i) => (
                  <div className="flex flex-wrap gap-1 md:justify-end">
                    <button onClick={() => setOpenCalls(openCalls === r.id ? null : r.id)}
                      data-testid={`sec-svc-activity-${i}`}
                      className="px-2 py-1 rounded border border-slate-300 bg-white text-xs font-bold">Activity</button>
                    {r.active && (
                      <button onClick={() => revoke(r)} data-testid={`sec-svc-revoke-${i}`}
                        className="px-2 py-1 rounded bg-rose-600 text-white text-xs font-bold flex items-center gap-1">
                        <Trash2 className="w-3 h-3" /> Revoke
                      </button>
                    )}
                  </div>) },
            ]} />
        )}
      </Card>

      {openCalls && <CallsCard token={token} accountId={openCalls} onChanged={reload} />}
    </>
  );
}

function CallsCard({ token, accountId, onChanged }) {
  const [onlyDenied, setOnlyDenied] = useState(false);
  const { data, loading, reload } = useApi(
    token, `${BASE}/${accountId}/calls?limit=100&only_denied=${onlyDenied}`, [accountId, onlyDenied]);
  const summary = data?.denied_summary || [];

  const ack = async () => {
    const r = await fetch(`${API}${BASE}/${accountId}/calls/ack`, { method: 'POST', headers: authHeaders(token) });
    if (!r.ok) { toast.error('Failed'); return; }
    const j = await r.json();
    toast.success(`${j.acked} refusals marked as seen`);
    reload(); onChanged?.();
  };

  const clear = async () => {
    if (!window.confirm('Delete the whole call journal for this key? The key keeps working.')) return;
    const r = await fetch(`${API}${BASE}/${accountId}/calls`, { method: 'DELETE', headers: authHeaders(token) });
    if (!r.ok) { toast.error('Failed'); return; }
    const j = await r.json();
    toast.success(`${j.deleted} records deleted`);
    reload(); onChanged?.();
  };

  return (
    <Card title="Recent calls" testid="sec-svc-calls-card"
      right={(
        <>
          <div className="inline-flex bg-slate-100 rounded-lg p-0.5">
            {[{ id: false, label: 'All' }, { id: true, label: 'Denied only' }].map((t) => (
              <button key={String(t.id)} onClick={() => setOnlyDenied(t.id)}
                data-testid={`sec-svc-calls-filter-${t.id ? 'denied' : 'all'}`}
                className={`px-2.5 py-1 rounded text-[11px] font-bold ${
                  onlyDenied === t.id ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}>
                {t.label}
              </button>
            ))}
          </div>
          <button onClick={ack} data-testid="sec-svc-ack"
            className="px-2.5 py-1 rounded-lg border border-slate-300 bg-white text-[11px] font-bold flex items-center gap-1">
            <CheckCheck className="w-3.5 h-3.5" /> Mark seen
          </button>
          <button onClick={clear} data-testid="sec-svc-clear"
            className="px-2.5 py-1 rounded-lg border border-rose-300 text-rose-700 bg-white text-[11px] font-bold flex items-center gap-1">
            <Eraser className="w-3.5 h-3.5" /> Clear log
          </button>
          <button onClick={reload} className="p-1.5 rounded-lg border border-slate-300">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </>
      )}>
      {!!summary.length && (
        <div className="mb-3 rounded-xl border border-rose-200 bg-rose-50 p-3" data-testid="sec-svc-denied-summary">
          <p className="text-xs font-bold text-rose-800 mb-1.5">What this key tried and was refused</p>
          <ul className="space-y-1">
            {summary.map((s, i) => (
              <li key={i} className="text-[11px] text-rose-900 flex flex-wrap gap-x-2">
                <span className="font-bold tabular-nums">{s.count}×</span>
                <span className="font-mono">{s.method} {s.sample_path}</span>
                <span className="text-rose-700">— {s.note}</span>
                <span className="text-rose-500">· last {fmtWhen(s.last)}</span>
              </li>
            ))}
          </ul>
          <p className="mt-1.5 text-[10px] text-rose-700">
            Last 24 h, unacknowledged only. Fixed something? Press “Mark seen” —
            the rows stay in the journal but stop being flagged.
          </p>
        </div>
      )}
      {!summary.length && !!data?.acked_denied && (
        <p className="mb-2 text-[11px] text-slate-500" data-testid="sec-svc-acked-note">
          No live refusals. {data.acked_denied} earlier refusal(s) marked as seen — still visible in the list below.
        </p>
      )}
      {loading ? <Spin /> : !data?.rows?.length ? <Empty>No calls recorded</Empty> : (
        <DataTable testid="sec-svc-calls-table" rows={data.rows}
          rowKey={(r) => r.id} rowTestid={(r, i) => `sec-svc-call-${i}`}
          cols={[
            { key: 'path', label: 'Endpoint', primary: true,
              render: (r) => <span className="font-mono text-[11px] break-all">{r.method} {r.path}</span> },
            { key: 'allowed', label: 'Result', primary: true,
              render: (r) => (
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                  r.allowed ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                  {r.status || (r.allowed ? 'ok' : 'denied')}
                </span>) },
            { key: 'ts', label: 'When', tdClass: 'text-slate-500 whitespace-nowrap', render: (r) => fmtWhen(r.ts) },
            { key: 'ip', label: 'IP', tdClass: 'font-mono text-slate-600', render: (r) => r.ip || '—' },
            { key: 'note', label: 'Note', full: true, tdClass: 'text-slate-500', render: (r) => r.note || '—' },
          ]} />
      )}
    </Card>
  );
}

export default ServiceAccountsTab;
